/** @format */

import store from '@/store'
import {
  Module, VuexModule, Mutation, Action,
} from 'vuex-module-decorators'
import router from '@/Routes'
import Vue from 'vue'
import User from '@/models/User'
import moment from 'moment'
import Api from '@/models/Api'
import WebMessage from '@/models/WebMessage'
import Forecast from '@/models/Forecast'
import { DeviceUUID } from 'device-uuid'
import axios from 'axios'
import tenancy from '@/plugins/tenancy'
import { getTurnstileToken } from '@/helpers/turnstile'
import WideNotification from './WideNotification'

@Module({ namespaced: true, store, name: 'system' })
export default class SystemtModule extends VuexModule {
  public login: boolean = false

  public _uuid: string = ''

  public device_uuid: string = ''

  public mfa_token: string = ''

  public login_error: string = ''

  public network: any = navigator.onLine

  public enviroment: string = process.env.VUE_APP_ENV || 'dev'

  public version: string = process.env.VUE_APP_VERSION || '1'

  public loading: any = {
    system: true as boolean,
    dashboard: false as boolean,
  }

  public token: string | null = localStorage.getItem('remember_token')

  public csrf: string | null = null

  public api_token: string = ''

  // Sanctum access token (per-device). Kept in memory only, never persisted.
  public sanctum_token: string = ''

  // Sanctum refresh token (per-device). Persisted; used only to mint access tokens.
  public refresh_token: string | null = localStorage.getItem('refresh_token')

  public last_update: string | null = null

  public user: User | null = null

  public api_domain: any = localStorage.getItem(`api:${window.location.origin}`)
    ? localStorage.getItem(`api:${window.location.origin}`)
    : process.env.VUE_APP_BASE_API_URL || ''

  public is_central: boolean = true

  public notifications: any = []

  public target: any = null

  public forecastModel: Forecast | null = null

  public is_quickbooks_connected: boolean = false

  public tenant_id: string = ''

  public mfa_alert_fired: boolean = false

  public env_vars: any = {}

  public filters: Array<any> = []

  public fields: any = {}

  public data: any = {
    // TODO: remove later
    target: null as any,
  }

  // Token used for bearer auth + ?api_token= transport: Sanctum access token wins,
  // legacy api_token is the coexistence fallback.
  public get authToken(): string {
    return this.sanctum_token || this.api_token
  }

  @Mutation
  public startLoading(tag: string) {
    if (tag == 'dashboard') {
      this.loading.dashboard = true
    } else {
      this.loading.system = true
    }
  }

  @Mutation
  public finishLoading(tag: string) {
    if (tag == 'dashboard') {
      this.loading.dashboard = false
    } else {
      this.loading.system = false
    }
  }

  @Mutation
  public updateState(payload: any) {
    switch (payload.name) {
      case 'target':
        this.target = payload.data
        this.data.target = this.target
        break
      case 'user':
        this.user = payload.data
        this.data.user = this.user
        break
      case 'notifications':
        this.notifications = payload.data
        break
      case 'env_vars':
        this.env_vars = payload.data
        break
      case 'is_quickbooks_connected':
        this.is_quickbooks_connected = payload.data
        break
      case 'tenant_id':
        this.tenant_id = payload.data
        break
      case 'forecast':
        this.forecastModel = payload.data
        break
      case 'filters':
        this.filters[payload.type] = payload.data
        break
      case 'fields':
        if (payload.type) {
          this.fields[payload.type] = payload.data
          localStorage.setItem('fields', JSON.stringify(this.fields))
        } else {
          this.fields = payload.data
        }
        break
    }
  }

  @Mutation
  public setUUID(uuid: string) {
    this._uuid = uuid
  }

  @Mutation
  public setMFAToken(token: string) {
    this.mfa_token = token
  }

  @Mutation
  public setMFAAlertFired(val: boolean) {
    this.mfa_alert_fired = val
  }

  @Mutation
  public setLoginError(error: string) {
    this.login_error = error
  }

  @Mutation
  public setDeviceUUID() {
    this.device_uuid = new DeviceUUID().get()
  }

  @Mutation
  public updateCSRF(token: string) {
    this.csrf = token
  }

  @Mutation
  public updateApiToken(token: string) {
    this.api_token = token
  }

  @Mutation
  public updateSanctumToken(token: string) {
    this.sanctum_token = token
  }

  @Mutation
  public updateRefreshToken(token: string) {
    this.refresh_token = token
    localStorage.setItem('refresh_token', token)
  }

  @Mutation
  public clearRefreshToken() {
    this.refresh_token = null
    localStorage.removeItem('refresh_token')
  }

  @Mutation
  public updatelastUpdate(date: string) {
    this.last_update = date
  }

  @Mutation
  public updateNetworkStatus(status: any) {
    this.network = status
  }

  @Mutation
  public clearData() {
    this.login = false
    this.network = navigator.onLine
    this.loading = {
      system: false as boolean,
      dashboard: false as boolean,
    }
    this.token = null
    this.csrf = null
    this.last_update = null
    this.user = null
    this.target = null
    this.api_token = ''
    this.sanctum_token = ''
    this.refresh_token = null
  }

  @Action
  public async init(token: string = '') {
    this.context.commit('updateState', {
      name: 'fields',
      data: JSON.parse(localStorage.getItem('fields') || '{}'),
    })
    this.context.commit('setDeviceUUID')
    this.context.commit(
      'setUUID',
      'xxxxxxxx-xxxx-xxxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0
        const v = c == 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
      }),
    )
    const { is_central } = tenancy({})

    if (!is_central) {
      const host = window.location.origin
      let domain: any = localStorage.getItem(`api:${host}`)

      if (!domain) {
        let api = new Api()
        api
          .post('tenant-front-url', {
            app_url: window.location.host,
          })
          .then(response => {
            let payload = response.data.result
            this.context.commit('setTenantFronendUrl', payload)
          })
      } else {
        this.context.commit('setTenantFronendUrl', { domain })
      }
    }

    // Get API Domain
    // const host = window.location.origin
    // let domain = localStorage.getItem(`api:${host}`)
    // let is_central = localStorage.getItem(`is_central:${host}`) === 'true'

    // if (!domain) {
    //   let api = new Api()
    //   api
    //     .post('tenant-front-url', {
    //       app_url: window.location.origin,
    //     })
    //     .then(response => {
    //       let payload = response.data.result
    //       this.context.commit('setTenantFronendUrl', payload)
    //     })
    // } else {
    //   this.context.commit('setTenantFronendUrl', { domain, is_central })
    // }

    if (this.user) {
      this.context.dispatch('sync')
    } else if (this.refresh_token) {
      // Per-device (Sanctum) boot: exchange the stored refresh token for a fresh access token.
      // The refresh token is ONLY ever sent as the bearer on this one endpoint.
      const api = new Api(false, {
        headers: { Authorization: `Bearer ${this.refresh_token}` },
      })
      api
        .post('user/session/refresh', {}, false)
        .then(response => {
          this.context.commit('updateSanctumToken', response.data.result.sanctum_token)
          this.context.dispatch('sync')
        })
        .catch(() => {
          // Refresh token expired/invalid — drop it and fall back to the legacy boot path.
          this.context.commit('clearRefreshToken')
          this.context.dispatch('legacyBoot', token)
        })
    } else {
      this.context.dispatch('legacyBoot', token)
    }
  }

  @Action
  public async legacyBoot(token: string = '') {
    if (this.token) {
      if (!token) {
        token = await getTurnstileToken('login')
      }
      const api = new Api()
      api
        .get(
          'user/login-token',
          {
            remember_token: this.token,
            token,
          },
          false,
        )
        .then(response => {
          // Parse and Store User object
          const user = User.toObject(response.data.result.user)

          // Save User into System State
          this.context.commit('updateState', {
            name: 'user',
            data: user,
          })

          if (user.isSuperAdmin) {
            localStorage.setItem('is_root_company', 'true')
          } else {
            localStorage.removeItem('is_root_company')
          }

          // Store api token
          if (response.data.result.api_token) {
            this.context.commit('updateApiToken', response.data.result.api_token)
          }

          // Store csrf token
          this.context.commit('updateCSRF', response.data.result.csrf)

          // Start data synchronization process.
          this.context.dispatch('sync')
        })
        .catch(e => {
          if (e.response.status === 403) localStorage.removeItem('remember_token')
          this.context.commit('finishLoading', 'system')
        })
    } else {
      this.context.commit('finishLoading', 'system')
      let is_root_company = localStorage.getItem('is_root_company')
      // @ts-ignore
      if (window.google && process.env.VUE_APP_GOOGLE_CLIENT_ID && is_root_company === 'true') {
        this.context.dispatch('googleSignIn')
      } else {
        setTimeout(() => {
          this.context.dispatch('googleSignIn')
        }, 1000)
      }
    }
  }

  @Action
  public googleSignIn() {
    // @ts-ignore
    if (!window.google) return
    // @ts-ignore
    window.google.accounts.id.initialize({
      client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
      auto_select: true,
      callback: (a: any) => {
        const api = new Api()
        api
          .get(`oauth/google/signin/${this.device_uuid}/${a.credential}`, {}, false)
          .then(response => {
            if (response.data.message === 'pending_mfa') {
              this.context.commit('setMFAToken', response.data.result.token)
              return
            }
            // Parse and Store User object
            const user = User.toObject(response.data.result.user)

            // Save User into System State
            this.context.commit('updateState', {
              name: 'user',
              data: user,
            })

            // Store Sanctum access token (in memory) — preferred over the legacy api_token.
            if (response.data.result.sanctum_token) {
              this.context.commit('updateSanctumToken', response.data.result.sanctum_token)
            }

            // Store api token
            if (response.data.result.api_token) {
              this.context.commit('updateApiToken', response.data.result.api_token)
            }

            // Persist the per-device refresh token when the backend issues one.
            if (response.data.result.refresh_token) {
              this.context.commit('updateRefreshToken', response.data.result.refresh_token)
            }

            // Store csrf token
            this.context.commit('updateCSRF', response.data.result.csrf)

            // Start data synchronization process.
            this.context.dispatch('sync')
          })
          .catch(e => {
            if (e.response.status === 403) localStorage.removeItem('remember_token')
            if (e.response.status === 422) {
              this.context.commit('setLoginError', e.response.data.message)
            }
            // @ts-ignore
            window.google.accounts.id.disableAutoSelect()
            this.context.commit('finishLoading', 'system')
          })
      },
    })
    // @ts-ignore
    window.google.accounts.id.prompt()
  }

  @Action
  public logout() {
    router.push('/login')
    // @ts-ignore
    if (window.google) window.google.accounts.id.disableAutoSelect()
    localStorage.removeItem('remember_token')
    localStorage.removeItem('refresh_token')
    this.context.commit('clearData')
  }

  @Action
  public getFilter(type: any) {
    return this.filters[type] ?? null
  }

  @Action
  public getFields(type: any) {
    return this.fields[type] ?? null
  }

  @Action
  public checkVersion(type: any) {
    axios.get('https://stag.dashboard.castiron.media/version.txt').then(response => {
      let remote = response.data
      // console.log(remote, this.version)
    })
  }

  @Action
  public trackEvent(name: string, data: any) {
    try {
      // @ts-ignore
      window.gtag('event', name, data)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  @Action
  public sync() {
    this.context.commit('startLoading', 'system')
    const api = new Api()
    api
      .get('sync')
      .then(response => {
        if (response.data.last_update) {
          this.context.commit(
            'updatelastUpdate',
            moment(response.data.last_update).format('MMMM, Do YYYY HH:mm Z'),
          )
        }

        // User
        const user = User.toObject(response.data.user)
        this.context.commit('updateState', {
          name: 'user',
          data: user,
        })

        try {
          // @ts-ignore
          window.gtag('config', 'G-F7EQMY5TWZ', {
            user_id: user.id,
            user_name: user.name,
            tenant_id: response.data.tenant_id,
            company: user.company.name,
            department: user.department ?? 'Unknown',
            user_type: user.type,
          })
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e)
        }

        // Notifications
        this.context.commit('updateState', {
          name: 'notifications',
          data: response.data.notifications.map((n: any) => {
            n.sub_type = n.data.sub_type
            return n
          }),
        })

        // Env vars
        this.context.commit('updateState', {
          name: 'env_vars',
          data: response.data.env_vars,
        })

        // Quickbooks
        this.context.commit('updateState', {
          name: 'is_quickbooks_connected',
          data: response.data.is_quickbooks_connected,
        })

        // Tenancy
        this.context.commit('updateState', {
          name: 'tenant_id',
          data: response.data.tenant_id,
        })

        Vue.prototype.$echo.options.auth.headers.Authorization = `Bearer ${this.authToken}`
        Vue.prototype.$echo.connector.pusher.config.auth = {
          headers: { Authorization: `Bearer ${this.authToken}` },
        }

        Vue.prototype.$echo.private(`user.${this.user!.id}`).listen('ReportReady', (e: any) => {
          if ((!e.instance_id && !this._uuid) || e.instance_id === this._uuid) {
            const api = new Api()

            api.download(`export-download?target=${encodeURIComponent(e.report)}`, e.output_name)

            this.context.commit('finishLoading', 'dashboard')
            WebMessage.success(
              "Your file is ready and should start soon. If it doesn't start click in the download button.",
              [
                {
                  text: 'Download',
                  action: () => {
                    const api = new Api()
                    api.download(
                      `export-download?target=${encodeURIComponent(e.report)}`,
                      e.output_name,
                    )
                  },
                },
              ],
            )
          }
        })

        Vue.prototype.$echo.private(`user.${this.user!.id}`).listen('MessageUI', (e: any) => {
          if (!e.instance_id || e.instance_id === this._uuid) {
            this.context.commit('finishLoading', 'dashboard')
            if (e.type === 'error') WebMessage.error(e.message)
            else if (e.type === 'success') WebMessage.success(e.message)
            else WebMessage.warning(e.message)
          }
        })

        Vue.prototype.$echo.private(`user.${this.user!.id}`).notification((notification: any) => {
          let notifications = this.notifications
          if (
            notification.action
            && notification.action.type
            && (!notification.instance_id || notification.instance_id === this._uuid)
          ) {
            notifications = WideNotification(notification, notifications)
          } else {
            notifications.unshift({
              created_at: moment().format('YYYY-MM-DD HH:mm:ss'),
              data: notification,
              id: notification.id,
              notifiable_id: null,
              notifiable_type: null,
              read_at: null,
              type: notification.type,
              sub_type: notification.sub_type,
              updated_at: moment().format('YYYY-MM-DD HH:mm:ss'),
            })
          }

          this.context.commit('updateState', {
            name: 'notifications',
            data: notifications,
          })
          if (!notification.instance_id || notification.instance_id === this._uuid) {
            if (!notification.silent) {
              const audio = new Audio('/notification.mp3')
              audio.volume = 0.7
              audio.play()
            }
            if (!notification.action || !notification.action.type) {
              WebMessage.info(notification.message)
            }
          }
        })

        if (router.currentRoute.name == 'Login') {
          let { target } = this
          if (target) {
            this.context.commit('updateState', {
              name: 'target',
              data: null,
            })
          }

          // TODO @tenancy-check
          // check if this will be needed in the future
          const { is_central } = tenancy({})

          if (!is_central) {
            if (!target) {
              if (this.user?.home?.includes('/')) {
                target = { path: this.user!.home }
              } else {
                target = { name: this.user!.home }
              }
            }
          } else {
            target = { name: 'Companies' }
          }

          setTimeout(() => {
            this.context.commit('finishLoading', 'system')
          }, 1000)

          router.push(target)
        } else {
          setTimeout(() => {
            this.context.commit('finishLoading', 'system')
          }, 500)
        }
      })
      .catch((e: any) => {
        this.context.commit('updateState', {
          name: 'user',
          data: null,
        })
        router.push('/login')
        this.context.commit('finishLoading', 'system')
      })
  }

  @Mutation
  public setTenantFronendUrl(payload: any) {
    const host = window.location.origin

    localStorage.setItem(`api:${host}`, payload.domain)
    this.api_domain = payload.domain
    this.is_central = false
  }
}
